import { AsyncLoadingState } from "@/features/dashboard/enums/common";
import { RootState } from "@/models/RootState.type";
import {
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

export enum WidgetStateEnum {
  COLLAPSED = "collapsed",
  EXPANDED = "expanded",
}

export interface WidgetState {
  assessment: {
    status: WidgetStateEnum;
    outcome: WidgetStateEnum;
  };
  instruct: {
    instructional: WidgetStateEnum;
  };
  tutor: {
    impact: WidgetStateEnum;
    highlights: WidgetStateEnum;
  };
  loading: AsyncLoadingState;
  showAssessmentWidgets: boolean;
  showTutorWidgets: boolean;
  showInstructWidgets: boolean;
  studentsNeedingAttention: number | null; // if null, then it is not loaded
}

const dashboardWidgetInitialState: WidgetState = {
  assessment: {
    status: WidgetStateEnum.COLLAPSED,
    outcome: WidgetStateEnum.COLLAPSED,
  },
  instruct: {
    instructional: WidgetStateEnum.EXPANDED,
  },
  tutor: {
    impact: WidgetStateEnum.COLLAPSED,
    highlights: WidgetStateEnum.COLLAPSED,
  },
  loading: AsyncLoadingState.LOADING,
  showAssessmentWidgets: false,
  showTutorWidgets: false,
  showInstructWidgets: false,
  studentsNeedingAttention: null,
};

const isAssessmentWidget = (widgetGroup: keyof WidgetState): widgetGroup is "assessment" => {
  return widgetGroup === "assessment";
};

const dashboardWidgetSlice = createSlice({
  name: "dashboardWidget",
  initialState: dashboardWidgetInitialState,
  reducers: {
    toggleWidget: (state, action: PayloadAction<{ group: keyof WidgetState; widget: string; }>) => {
      const { group, widget } = action.payload;
      const widgetState = state[group] as Record<string, WidgetStateEnum>;
      const isExpanded = widgetState[widget] === WidgetStateEnum.EXPANDED;

      if (isAssessmentWidget(group)) {
        widgetState.status = isExpanded ? WidgetStateEnum.COLLAPSED : WidgetStateEnum.EXPANDED;
        widgetState.outcome = isExpanded ? WidgetStateEnum.COLLAPSED : WidgetStateEnum.EXPANDED;
      } else {
        widgetState[widget] = isExpanded ? WidgetStateEnum.COLLAPSED : WidgetStateEnum.EXPANDED;
      }
    },
    updateSubscriptionInfo: (state, action) => {
      const { assessmentWidgetStatus, tutorWidgetStatus, instructWidgetStatus } = action.payload;
      if (!assessmentWidgetStatus && !tutorWidgetStatus && !instructWidgetStatus) {
        state.loading = AsyncLoadingState.ERROR;
        return;
      }
      state.showAssessmentWidgets = assessmentWidgetStatus;
      state.showTutorWidgets = tutorWidgetStatus;
      state.showInstructWidgets = instructWidgetStatus;
      state.loading = AsyncLoadingState.SUCCESS;
    },
    setLoading: (state, action: PayloadAction<AsyncLoadingState>) => {
      state.loading = action.payload;
    },
    setStudentsNeedingAttention: (state, action: PayloadAction<number>) => {
      state.studentsNeedingAttention = action.payload;
    }
  },

});

export const { toggleWidget, updateSubscriptionInfo, setLoading, setStudentsNeedingAttention } = dashboardWidgetSlice.actions;
export const dashboardWidgetState = (state: RootState) => state.dashboardWidget;
export default dashboardWidgetSlice.reducer;
